import { GetStaticProps } from 'next';

import { HomePageFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import { getServerPageGetHome } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import HomepageHeader from 'modules/homepage-header/homepage-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { Head } from 'modules/head/head';
import { logger } from 'utils/logger';

interface IHomePageProps extends IBasePageProps {
	homePage?: HomePageFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
}

const HomePage = (props: IHomePageProps): React.JSX.Element => {
	const modules = props.homePage?.modules;
	const seoModule = props.homePage?.seo;

	return (
		<Layout
			contentId={props.id}
			typeName={props?.homePage?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<HomepageHeader module={props?.homePage?.header} />
			<ModuleBuilder modules={modules} slug={ROUTES.HOME.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const homePageResult = await getServerPageGetHome(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);

		const { homePage, pageInformation } = homePageResult.props.data;

		if (homePage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				homePage: homePage.data?.attributes,
				pageInformation: pageInformation?.data?.attributes,
				id: homePage.data?.documentId,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'home-page',
						contentTypeId: homePage?.data?.documentId
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error) {
		logger.staticPropsError(
			'Failed to generate static props for Gelderland homepage',
			error as Error,
			'/',
			ctx.locale,
			{
				preview: ctx.preview,
				site: 'gelderland'
			}
		);

		return {
			notFound: true
		};
	}
};

export default HomePage;
